import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Components
import { Button } from "@gpstrackit/react-controls"
import { Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, CircularProgress } from "@mui/material"

//GraphQL
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';

//Views
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import ChipStatus from 'Components/ChipStatus';
import CreateEditBillingAccount from 'Modules/dealers/views/.components/createEditBillingAccount';
import DeleteBillingAccount from '../deleteBillingAccount';

//Security
import { HasPermission } from "Core/security";

//Notification
import { NotificationManager } from "Components/Notifications";

//Helpers
import { getDateFormatted, getPMUser } from 'Core/data/Helpers';

//Actions
import {
    searchEntityRequest,
    openModalDeleteBillingAccount,
    openModalCreateEditBillingAccount,
    clearRedux
} from 'Redux/actions';
import { get } from "lodash";

const BillingAccountsTab = (props) => {

    const { height, dealerId } = props;

    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();


    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const lang = useSelector(state => state.commonRedux.language);
    const user = useSelector(state => state.securityRedux.user);
    const createEditBillingAccountOpenModal = useSelector(state => state.createEditBillingAccountRedux.openModal);
    const openModalDelete = useSelector(state => state.deleteBillingAccountRedux.openModal);

    const [columns, setColumns] = useState([
        {
            key: "id",
            field: "id",
            title: messages['billing_accounts_id'],
            width: 100,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "billing_accounts_id",
            render: (item) => {
                return <span>{item?.id}</span>
            },
            export: true,
            functionString: 'return row?.id'
        },
        {
            key: "name",
            field: "name",
            title: messages['billing_accounts_name'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            code: "billing_accounts_name",
            render: (item) => {
                return (
                    <span>{item?.name || ''}</span>
                )
            },
            export: true,
            functionString: 'return row?.name'
        },
        {
            key: "devicesAssigned",
            field: "assignedUnitsCount",
            title: messages['billing_accounts_devices_assigned'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            code: "billing_accounts_devices_assigned",
            render: (item) => {
                const count = item?.assignedUnitsCount || 0;
                if (count === 0) return <span>0</span>;
                return (
                    <a
                        style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                        onClick={() => openDevicesModal(item)}
                    >
                        {count}
                    </a>
                )
            },
            export: true,
            functionString: 'return row?.assignedUnitsCount || 0'
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['billing_accounts_created_on'],
            show: true,
            resize: true,
            width: 200,
            sort: true,
            export: true,
            functionString: 'return row?.createdOn && (new Date(`${row?.createdOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "billing_accounts_created_on",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime")}</span>
            }
        },
        {
            key: "createdBy",
            field: "createdBy",
            title: messages['billing_accounts_created_by'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                let pmUsers = ${JSON.stringify(pmUsers)}
                pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
            `,
            code: "billing_accounts_created_by",
            render: (item) => {
                return <span>{item?.createdBy && getPMUser(item?.createdBy, pmUsers)}</span>
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['billing_accounts_updated_on'],
            show: true,
            resize: true,
            width: 200,
            sort: true,
            export: true,
            functionString: 'return row?.updatedOn && (new Date(`${row?.updatedOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "billing_accounts_updated_on",
            render: (item) => {
                return <span>{item?.updatedOn && getDateFormatted(item?.updatedOn, "date", "datetime")}</span>
            }
        },
        {
            key: "updatedBy",
            field: "updatedBy",
            title: messages['billing_accounts_updated_by'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                let pmUsers = ${JSON.stringify(pmUsers)}
                pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.updatedBy)
                return pmUsers.firstName + " " + pmUsers.lastName || row?.updatedBy
            `,
            code: "billing_accounts_updated_by",
            render: (item) => {
                return <span>{item?.updatedBy && getPMUser(item?.updatedBy, pmUsers)}</span>
            }
        }

    ])

    const [searchFields, setSearchFields] = useState(["name"]);
    const [devicesModal, setDevicesModal] = useState({ open: false, name: null });
    const [devicesList, setDevicesList] = useState([]);
    const [loadingDevices, setLoadingDevices] = useState(false);

    const openDevicesModal = async (item) => {
        setDevicesModal({ open: true, name: item.name });
        setLoadingDevices(true);
        setDevicesList([]);
        try {
            const response = await clientQuery(`
                query searchUnits($conditions: [UnitSearchInput], $limit: Int, $offset: Int) {
                    result: searchUnits(conditions: $conditions, limit: $limit, offset: $offset) {
                        items { id label esn }
                    }
                }`,
                {
                    conditions: [{ fields: ["unitConfig_billingAccount_name"], terms: [item.name], exact: true }],
                    limit: 500,
                    offset: 0
                },
                endpoints.GRAPHQL_GENERAL
            );
            setDevicesList(response?.result?.items || []);
        } catch (e) {
            setDevicesList([]);
        }
        setLoadingDevices(false);
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW CONFIGURATION OBJECTS
    // ═══════════════════════════════════════════════════════════════════════════

    const graphqlConfig = useMemo(() => ({
        function: "searchBillingAccounts",
        sortType: "BillingAccountSortInput",
        conditionsType: "BillingAccountSearchInput",
        customQuery: "id assignedUnitsCount",
        dynamicQuery: true,
    }), []);

    const searchConfig = useMemo(() => ({
        fields: ["name"],
        optionValue: "id",
        optionLabel: "name",
        custom: { fields: ["dealerId"], terms: [dealerId], exact: true, conditionOperator: "AND" },
    }), [dealerId]);

    const uiConfig = useMemo(() => ({
        hideFilters: true,
        hideSplit: true,
        topHeight: props?.single ? 286 : 200,
        height: height && height + "px" || "500px",
    }), [height]);

    const exportConfig = useMemo(() => ({
        fileName: "billing-accounts-tab",
    }), []);

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW HANDLERS
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Handle condition changes from search/filters
     */
    const handleChangeConditions = useCallback((tags, conditions, total, newColumns, rows, sort) => {
        //current conditions
        let currentConditions = {}
        let currentTags = {}

        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
        }

        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name' && item?.fields[0] !== 'dealerId');

        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            //New Items
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }

        let tagsByLabel = currentTags.filter((item) => typeof item.id === "string");

        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.name))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        currentConditions.push({ fields: ["dealerId"], terms: [dealerId], exact: true, conditionOperator: "AND" });

        if (!conditions && tags) tableRef?.current?.getData({ newConditions: currentConditions });
        else tableRef?.current?.getData({ newRows: rows, newSort: sort, newConditions: currentConditions, newColumns });
    }, [dealerId, searchFields]);

    /**
     * Handle column changes (reorder, resize, visibility)
     */
    const handleChangeColumns = useCallback((items) => {
        let currentColumns = items.map((item) => ({
            ...item,
            title: item.code ? messages[item.code] : item.title,
            render: item.key === "status" ? (item) => {
                return (
                    <div style={{ display: 'flex' }}>
                        <ChipStatus statusCode={item?.status} messages={messages} />
                    </div>
                )
            } : item.render,
        }))
        setColumns(currentColumns)
    }, [messages]);

    /**
     * Context menu options for row actions
     */
    const menuOptions = useMemo(() => [
        {
            id: "billing_accounts_edit",
            label: messages['billing_accounts_edit'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "bllngaccnts", "u"),
            action: (items) => {
                let currentItem = items.find(item => item.id);
                dispatch(openModalCreateEditBillingAccount(currentItem))
            }
        },
        {
            id: "billing_accounts_delete",
            label: messages['billing_accounts_delete'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "bllngaccnts", "d"),
            action: (items) => {
                let currentItem = items.find(item => item.id);
                dispatch(openModalDeleteBillingAccount({ billingAccount: currentItem, dealerId: dealerId }))
            }
        }
    ], [messages, user, dispatch, dealerId]);

    /**
     * Menu options labels for context menu
     */
    const getMenuOptions = useCallback(() => {
        return [
            messages['billing_accounts_edit'],
            messages['billing_accounts_delete']
        ]
    }, [messages]);

    /**
     * Handle click on context menu item
     */
    const handleClickMenu = useCallback((event, item) => {
        if (event === messages['billing_accounts_edit']) {
            if (HasPermission(user, "bllngaccnts", "u")) {
                dispatch(openModalCreateEditBillingAccount(item))
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['billing_accounts_title'],
                    5000,
                );
            }
        }
        if (event === messages['billing_accounts_delete']) {
            if (HasPermission(user, "bllngaccnts", "d")) {
                dispatch(openModalDeleteBillingAccount({ billingAccount: item, dealerId: dealerId }));
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['billing_accounts__title'],
                    5000,
                );
            }
        }
    }, [messages, user, dispatch, dealerId]);

    /**
     * Render extra button for adding new billing account
     */
    const renderExtraButton = useCallback(() => {
        return (
            <div style={{ margin: '0 5px' }}>
                <Button
                    disabled={!HasPermission(user, "bllngaccnts", "c")}
                    className="btn-dark-blue"
                    onClick={(e) => {
                        e.preventDefault();
                        dispatch(openModalCreateEditBillingAccount())
                    }}
                >
                    {messages['billing_accounts_add_new']}
                </Button>
            </div>
        )
    }, [user, dispatch, messages]);

    useEffect(() => {
        return () => {
            dispatch(clearRedux("COMMON", {
                'billing-accounts-tab': {}
            }))
        }
    }, [])

    useEffect(() => {
        (!pmUsers || pmUsers.length === 0) && dispatch(searchEntityRequest({ key: "pmUsers" }));
    }, []);

    useEffect(() => {
        if (pmUsers.length > 0) {
            let currentColumns = columns.map((item) => ({
                ...item,
                title: messages[item.code],
                functionString: item.key === "createdBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
                ` : item.key === "updatedBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.updatedBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.updatedBy
                ` : item.functionString,
                render: item.key === "createdBy" ? (item) => {
                    let label = item?.createdBy ? getPMUser(item?.createdBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.key === "updatedBy" ? (item) => {
                    let label = item?.updatedBy ? getPMUser(item?.updatedBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.render,
            }))
            setColumns([...currentColumns])
        }
    }, [pmUsers, lang])

    return (
        <div className="billing-accounts-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                name="pm-billing-accounts-tab"
                entity="billing-accounts-tab"
                columns={columns}
                infiniteScroll
                graphqlConfig={graphqlConfig}
                searchConfig={searchConfig}
                uiConfig={uiConfig}
                exportConfig={exportConfig}
                onChangeConditions={handleChangeConditions}
                onChangeColumns={handleChangeColumns}
                tableButtons={["export", 'orderColumn']}
                options={menuOptions}
                menuOptions={getMenuOptions}
                onClickMenu={handleClickMenu}
                extraButton={renderExtraButton}
            />
            {createEditBillingAccountOpenModal && <CreateEditBillingAccount tableRef={tableRef} dealerId={dealerId} />}
            {openModalDelete && <DeleteBillingAccount tableRef={tableRef} />}

            <Dialog
                open={devicesModal.open}
                onClose={() => setDevicesModal({ open: false, name: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {messages['billing_accounts_assigned_devices_title']} — {devicesModal.name}
                </DialogTitle>
                <DialogContent dividers>
                    {loadingDevices ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                            <CircularProgress />
                        </div>
                    ) : devicesList.length === 0 ? (
                        <span>{messages['billing_accounts_no_devices']}</span>
                    ) : (
                        <List dense>
                            {devicesList.map(device => (
                                <ListItem key={device.id}>
                                    <ListItemText
                                        primary={device.label || device.esn}
                                        secondary={device.label ? device.esn : null}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        className="btn-cancel"
                        onClick={() => setDevicesModal({ open: false, name: null })}
                    >
                        {messages['common_cancel']}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
export default BillingAccountsTab;